import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BearerToken } from '@marageti/z4-sdk/lib/auth';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import {
  hasAuthorizedRole,
  LocalStorageService,
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import { FieldValues } from 'react-hook-form';
import { TimeZoneMap } from '@marageti/z4-sdk/lib/standards';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import VerificationView from './verification-view';
import ConfirmPhoneView from './confirm-phone-view';
import { useAuthStore } from '../../store';
import { authorizedRoles, getMatchingUser } from '../../utils/auth';
import SetPasswordView from './set-password-view';
import SkeletonTripDetailsBackground from './skeleton-trip-details-background';

const DEFAULT_VALUES = {
  phone: {
    prefix: '',
    number: '',
  },
  timeZone: {
    name: '',
    rawOffsetInMinutes: 0,
  },
};

const LocalStorage = new LocalStorageService();

const VerificationContainer = () => {
  const {
    loggedInTraveler,
    removeAuthenticatedTraveler,
    setIsAuthenticated,
    setAuthenticatedTraveler,
  } = useAuthStore();
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [search, setSearch] = useSearchParams();
  const email = search.get('email');
  const photoId = search.get('photo');
  const destinationsQueryParam = search.get('destinations');

  const token = search.get('code');
  const redirect = search.get('redirect');
  const tripRequestId = search.get('tripRequestId');
  const tripRequestDestination = tripRequestId ? `/trip-requests/${tripRequestId}` : undefined;

  const timezones = useFetchImmutable<TimeZoneMap>(apiClient.standardsClient.getTimeZones, [], true);
  const timezonesMap = timezones.data ? new Map(Object.entries(timezones.data)) : new Map();
  const suggestedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ');
  const suggestedTimezoneOption = timezonesMap.has(suggestedTimezone) ? {
    name: suggestedTimezone,
    rawOffsetInMinutes: timezonesMap.get(suggestedTimezone),
  }
    : DEFAULT_VALUES.timeZone;

  const destinationCountryNames = useMemo(() => {
    if (destinationsQueryParam) {
      return destinationsQueryParam.split(',').join(', ');
    }
    return null;
  }, [destinationsQueryParam]);

  const [loginError, setLoginError] = useState<ApiError>();
  const [tokenExpired, setTokenExpired] = useState(!email);
  const [newLinkSent, setNewLinkSent] = useState(false);
  const [showConfirmPhone, setShowConfirmPhone] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [verificationError, setVerificationError] = useState<ApiError | undefined>();

  useEffect(() => {
    const checkTravelerStatus = async () => {
      if (email) {
        try {
          const status = await apiClient.peopleClient.getTravelerStatus(email);
          if (status && status.status === 'ENABLED') {
            let destination = '/login';
            if (tripRequestId) {
              const redirectPath = `/trip-requests/${tripRequestId}`;
              destination = `/login?redirect=${encodeURIComponent(redirectPath)}`;
            }
            navigate(destination, { replace: true });
          }
        } catch (error) {
          console.error('Failed to get traveler status:', error);
        }
      }
    };

    checkTravelerStatus();
  }, [email, tripRequestId]);

  const onActivationSuccess = async (bearerToken: BearerToken): Promise<void> => {
    const { accessToken } = bearerToken;
    if (email) {
      LocalStorage.addToLocalStorage('accessToken', accessToken);
      LocalStorage.addToLocalStorage('email', email);
      apiClient.instance.setAuthToken(accessToken);

      try {
        const data: Traveler = await apiClient.peopleClient.getTraveler(email);
        const matchingUser = getMatchingUser(data.email, data.userInfo);

        if (!matchingUser || !hasAuthorizedRole(matchingUser.roles, authorizedRoles, true)) {
          throw new Error('Not Authorized');
        }

        search.delete('email');
        search.delete('token');
        setSearch(search);
        setAuthenticatedTraveler(data);
        setShowConfirmPhone(true);
      } catch (e) {
        removeAuthenticatedTraveler();
        setIsAuthenticated(false);
        setLoginError(e as ApiError);
      }
    }
  };

  const handleResendVerificationCode = async (): Promise<void> => {
    if (email) {
      try {
        const params = tripRequestId ? { tripRequestId } : undefined;
        await apiClient.authClient.postUserResetCode(email, { params });
        setNewLinkSent(true);
      } catch (error) {
        console.error('Failed to resend verification code:', error);
        setVerificationError(error as ApiError);
      }
    }
  };

  const handleVerificationSubmit = async (formData: { code: string[] }): Promise<void> => {
    const code = formData.code.join('');
    setVerificationError(undefined);

    if (!email) {
      setVerificationError({ message: 'Email is required' } as ApiError);
      return;
    }

    try {
      const response = await apiClient.authClient.resetTokenExists(email, code);
      if (response) {
        search.set('code', code);
        setSearch(search);
        setShowSetPassword(true);
      } else {
        setVerificationError({ message: 'Invalid verification code. Please try again.' } as ApiError);
      }
    } catch (error) {
      setVerificationError(error as ApiError);
    }
  };

  // Username and password activation and login
  const onPasswordSubmit = async (data: FieldValues) => {
    if (!email || !token) {
      setTokenExpired(true);
      navigate('/reset/password');
      return;
    }

    setLoginError(undefined);
    try {
      const res = await apiClient.authClient.putUserResetToken(email, token, { password: data.password });
      onActivationSuccess(res);
    } catch (error) {
      setLoginError(error as ApiError);
      setTokenExpired(true);
      navigate('/reset/password');
    }
  };

  const onPhoneSubmit = async (data: FieldValues) => {
    if (loggedInTraveler) {
      const { phone, timeZone } = data;
      const request = {
        ...loggedInTraveler,
        ...(phone.number && { phone: data.phone }),
        ...(timeZone.name && { timeZone: data.timeZone }),
      };
      try {
        const res: Traveler = await apiClient.peopleClient.putTraveler(loggedInTraveler.id, request);
        setAuthenticatedTraveler(res);
      } catch (error) {
        console.error('Failed to submit phone:', error);
      } finally {
        setIsAuthenticated(true);
        navigate(tripRequestDestination || redirect || '/');
      }
    } else {
      setIsAuthenticated(true);
      navigate(tripRequestDestination || redirect || '/');
    }
  };

  const onResendLinkSuccess = () => {
    setNewLinkSent(true);
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        py: { mobile: 0, md: 4 },
        bgcolor: 'transparent',
      }}
    >
      {isDesktop && <SkeletonTripDetailsBackground photoId={photoId} destinations={destinationCountryNames} />}
      {showConfirmPhone && (
        <ConfirmPhoneView
          defaultValues={{
            phone: loggedInTraveler?.phone || DEFAULT_VALUES.phone,
            timeZone: loggedInTraveler?.timeZone.name ? loggedInTraveler?.timeZone : suggestedTimezoneOption,
          }}
          onSubmit={onPhoneSubmit}
        />
      )}
      {showSetPassword && !showConfirmPhone && (
        <SetPasswordView
          email={email!}
          loginError={loginError}
          newLinkSent={newLinkSent}
          onPasswordSubmit={onPasswordSubmit}
          onResendLinkSuccess={onResendLinkSuccess}
          tokenExpired={tokenExpired}
        />
      )}
      {!showConfirmPhone && !showSetPassword && (
        <VerificationView
          email={email!}
          onSubmit={handleVerificationSubmit}
          submissionError={verificationError}
          clearSubmissionError={() => setVerificationError(undefined)}
          onResendCode={handleResendVerificationCode}
          initialCode={token || undefined}
        />
      )}
    </Box>
  );
};

export default VerificationContainer;
